import { Container, Flex, Paper, Text, Badge, Tabs, Anchor, Title, Box, Button, Divider } from "@mantine/core";
import {
  IconBuilding,
  IconMapPin,
  IconPhone,
  IconMail,
  IconRosetteDiscountCheckFilled,
  IconWorld,
  IconBrandLinkedin,
  IconExternalLink,
  IconArrowLeft,
} from "@tabler/icons-react";
import MatchedProfileTab from "@/pages/profile/MatchedPrfofielTab";
import StatusBadge from "@/pages/profile-verification/StatusBadge";
import DocumentsTab from "@/components/shared/AnonymousCompanyProfile/DocumentsTab";
import ImagesTabContent from "@/components/profile/ImagesTabContent";
import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import { getFileUrl } from "@/shared/utils/useFileUrl";

interface MatchedProfileViewProps {
  company?: any;
  containerSize?: "fluid";
  isOwnProfile?: boolean;
}

export default function MatchedProfileView({ company: propCompany, containerSize = "fluid", isOwnProfile = false }: MatchedProfileViewProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails, hasProfile } = useUserContext();

  const [company, setCompany] = useState<any>(propCompany || null);
  const [collaboration, setCollaboration] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string | null>("profile");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // Check if we're on the collaboration profile route
  const isCollaborationProfile =
    location.pathname.includes("/collaborations/") && location.pathname.includes("/profile");

  useEffect(() => {
    // Only fetch if we don't have company data from props and we're on collaboration profile route
    if (hasProfile === null) return;
    if (!propCompany && isCollaborationProfile && id) {
      const fetchCompanyData = async () => {
        try {
          const collabRecord = await trpc.collaboration.getById.query({ matchId: id });
          setCollaboration(collabRecord);

          const myCompanyId = userDetails?.company?.id;
          const otherCompanyId =
            collabRecord.requesterCompanyId === myCompanyId
              ? collabRecord.receiverCompanyId
              : collabRecord.requesterCompanyId;

          const companyData = await trpc.company.getById.query({ companyId: otherCompanyId });
          setCompany(companyData);
        } catch (err) {
          console.error("Error fetching company:", err);
          handleTrpcError(err);
        }
      };

      fetchCompanyData();
    }
  }, [id, propCompany, isCollaborationProfile, userDetails?.company?.id, hasProfile]);

  // Fetch logo URL from S3 when company changes
  useEffect(() => {
    if (company?.logo?.id) {
      getFileUrl(company.logo.id).then((url) => {
        setLogoUrl(url);
      });
    }
  }, [company]);

  // Safety check - if no company data, show error
  if (!company) {
    return (
      <Container size={containerSize} py="xl">
        <Text>No company data available</Text>
      </Container>
    );
  }

  return (
    <Box>
      {/* Back Button - Only show on collaboration profile route */}
      {isCollaborationProfile && (
        <Box py="sm" px="xl">
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            size="sm"
            onClick={() => navigate(`/collaborations/${id}`)}
          >
            Back to Workspace
          </Button>
        </Box>
      )}

      <Container size={containerSize} pb="xs">
        {/* Company Header - Shows REAL data */}
        <Paper p="xl" radius="lg" mb="xl" style={{ border: "1px solid #E5E7EB" }}>
          <Flex gap="md" align="flex-start" justify="space-between">
            <Flex
              w={{ base: 48, sm: 80 }}
              h={{ base: 48, sm: 80 }}
              style={{
                borderRadius: 8,
                border: "1px solid #E5E7EB",
                backgroundColor: "#F9FAFB",
                flexShrink: 0,
              }}
              align="center"
              justify="center"
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Company Logo"
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                />
              ) : (
                <IconBuilding size={32} color="#6B7280" />
              )}
            </Flex>

            <Flex direction="column" style={{ flex: 1 }} gap={6}>
              {/* Row 1: Company Name + Verified Badge */}
              <Flex align="center" gap="sm">
                <Title order={2} fw={400} fz={22}>
                  {company.legalName || "Company Name"}
                </Title>
                {company.status === "VERIFIED" && (
                  <IconRosetteDiscountCheckFilled size={20} style={{ color: "var(--mantine-color-primary-6)" }} />
                )}
              </Flex>

              {/* Row 2: GTID Badge */}
              {company.gtid && (
                <Badge
                  variant="light"
                  color="#EEF2F7"
                  size="sm"
                  radius="sm"
                  style={{
                    width: "fit-content",
                    backgroundColor: "#EEF2F7",
                    color: "#374151",
                  }}
                >
                  {company.gtid}
                </Badge>
              )}

              {/* Row 3: Location, Phone, Email */}
              <Flex gap="md" wrap="wrap" align="center">
                <Flex align="center" gap={6}>
                  <IconMapPin size={16} color="#6B7280" />
                  <Text size="sm" c="slate.7">
                    {company.headquartersCity && company.headquartersCountry
                      ? `${company.headquartersCity}, ${company.headquartersCountry}`
                      : company.headquartersCountry || "N/A"}
                  </Text>
                </Flex>
                <Flex align="center" gap={6}>
                  <IconPhone size={16} color="#6B7280" />
                  <Text size="sm" c="slate.7">
                    {company.phoneCountryCode && company.phoneNumber
                      ? `${company.phoneCountryCode} ${company.phoneNumber}`
                      : "N/A"}
                  </Text>
                </Flex>
                <Flex align="center" gap={6}>
                  <IconMail size={16} color="#6B7280" />
                  <Text size="sm" c="slate.7" style={{ wordBreak: "break-all" }}>
                    {company.email || "N/A"}
                  </Text>
                </Flex>
              </Flex>

              {/* Row 4: Website and LinkedIn */}
              <Flex gap="md" wrap="wrap" align="center">
                {company.website && (
                  <Anchor
                    href={company.website}
                    target="_blank"
                    td="none"
                    styles={{
                      root: {
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      },
                    }}
                  >
                    <Flex align="center" gap={6}>
                      <IconWorld size={16} color="var(--mantine-color-primary-6)" />
                      <Text size="sm" c="primary.6">
                        Website
                      </Text>
                      <IconExternalLink size={14} color="var(--mantine-color-primary-6)" />
                    </Flex>
                  </Anchor>
                )}
                {company.linkedinUrl && (
                  <Anchor
                    href={company.linkedinUrl}
                    target="_blank"
                    td="none"
                    styles={{
                      root: {
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      },
                    }}
                  >
                    <Flex align="center" gap={6}>
                      <IconBrandLinkedin size={16} color="var(--mantine-color-primary-6)" />
                      <Text size="sm" c="primary.6">
                        LinkedIn
                      </Text>
                    </Flex>
                  </Anchor>
                )}
              </Flex>
            </Flex>
            {isOwnProfile && ["UNDER_REVIEW", "VERIFIED", "SYSTEM_VERIFIED"].includes(company.status) && (
              <StatusBadge status={company.status} />
            )}
          </Flex>
        </Paper>

        {/* Tabs Section */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List
            styles={{
              list: {
                flexWrap: "wrap",
              },
            }}
          >
            <Tabs.Tab
              value="profile"
              px={{ base: "xs", sm: "md" }}
              fz={{ base: "xs", sm: "sm" }}
              style={{
                color: activeTab === "profile" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
                whiteSpace: "nowrap",
              }}
            >
              Company Profile
            </Tabs.Tab>
            <Tabs.Tab
              value="images"
              px={{ base: "", sm: "md" }}
              fz={{ base: "xs", sm: "sm" }}
              c={activeTab === "images" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)"}
              style={{
                whiteSpace: "nowrap",
              }}
            >
              Images and Documents
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="profile" pt="xl">
            <MatchedProfileTab company={company} collaboration={collaboration} />
          </Tabs.Panel>

          <Tabs.Panel value="images" pt="xl">
            <Text size="lg" fw={400} mb="md">
              Images
            </Text>
            {activeTab === "images" && (
              <ImagesTabContent
                company={company}
                activeTab={activeTab}
                refreshCompany={async () => {}}
                isViewMode={true}
              />
            )}
            <Divider my="xl" />
            <Text size="lg" fw={400} mb="md">
              Documents
            </Text>
            <DocumentsTab
              company={{
                ...company,
                documents: (company.documents || []).filter(
                  (d: any) => !["AFFIDAVIT", "AUTHORITY_LETTER"].includes(d.category)
                ),
              }}
              isMatchedView={true}
            />
          </Tabs.Panel>
        </Tabs>

        {/* About Verified Listings */}
        <Paper p="xl" radius="lg" mt="xl" style={{ border: "1px solid #E5E7EB" }}>
          <Text size="md" fw={400} mb="md">
            About Verified Listings
          </Text>
          <Flex gap="md" wrap="wrap">
            <Flex align="center" gap="xs" style={{ flex: "1 1 48%", minWidth: "280px" }}>
              <Text size="sm" c="slate.6">
                •
              </Text>
              <Text size="sm" c="slate.6">
                Full company details are visible after mutual interest is confirmed
              </Text>
            </Flex>
            <Flex align="center" gap="xs" style={{ flex: "1 1 48%", minWidth: "280px" }}>
              <Text size="sm" c="slate.6">
                •
              </Text>
              <Text size="sm" c="slate.6">
                Contact information becomes accessible to matched companies
              </Text>
            </Flex>
            <Flex align="center" gap="xs" style={{ flex: "1 1 48%", minWidth: "280px" }}>
              <Text size="sm" c="slate.6">
                •
              </Text>
              <Text size="sm" c="slate.6">
                Verification badge confirms company authenticity
              </Text>
            </Flex>
            <Flex align="center" gap="xs" style={{ flex: "1 1 48%", minWidth: "280px" }}>
              <Text size="sm" c="slate.6">
                •
              </Text>
              <Text size="sm" c="slate.6">
                Both parties can initiate collaboration discussions
              </Text>
            </Flex>
          </Flex>
        </Paper>
      </Container>
    </Box>
  );
}
